import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Download, Upload, AlertCircle, CheckCircle2, Info, Sparkles, Zap } from 'lucide-react';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { ImportTypeSelector, ImportType } from '@/components/admin/import/ImportTypeSelector';
import { FileUpload } from '@/components/admin/import/FileUpload';
import { FieldMapping } from '@/components/admin/import/FieldMapping';

const DataImport: React.FC = () => {
  const { organizationId } = useAuth();
  const [activeTab, setActiveTab] = useState('ai-import');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    details?: string;
  } | null>(null);

  // AI Import state
  const [aiImportStep, setAiImportStep] = useState<'select' | 'upload' | 'mapping' | 'importing'>('select');
  const [selectedImportType, setSelectedImportType] = useState<ImportType | null>(null);
  const [fileAnalysis, setFileAnalysis] = useState<any>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleDownloadTemplate = () => {
    // Create template data based on the active tab
    let csvContent = '';
    
    switch (activeTab) {
      case 'families':
        csvContent = 'name,billing_status,notes\nSmith Family,current,Example family\nJones Family,current,Another example';
        break;
      case 'parents':
        csvContent = 'family_name,first_name,last_name,email,phone,is_primary,address_street,address_city,address_state,address_zip,address_country\nSmith Family,John,Smith,john@example.com,(555) 123-4567,true,123 Main St,Anytown,CA,12345,United States\nSmith Family,Jane,Smith,jane@example.com,(555) 987-6543,false,123 Main St,Anytown,CA,12345,United States';
        break;
      case 'students':
        csvContent = 'family_name,first_name,last_name,date_of_birth,gender,notes\nSmith Family,Emily,Smith,2015-05-12,F,Allergic to peanuts\nJones Family,Jack,Jones,2014-10-30,M,';
        break;
      default:
        csvContent = 'No template available for this type';
    }
    
    // Create a blob from the CSV content
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element and trigger download
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${activeTab}_import_template.csv`;
    
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleUpload = async () => {
    if (!selectedFile || !organizationId) {
      toast.error('Please select a file and ensure you are logged in');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      // Simulate progress for demo
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + 10;
          if (newProgress >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return newProgress;
        });
      }, 300);

      // Here you would normally send the file to your server
      // For this example, we'll just simulate a successful upload
      await new Promise(resolve => setTimeout(resolve, 3000));
      clearInterval(progressInterval);
      setUploadProgress(100);

      setUploadResult({
        success: true,
        message: 'Upload completed successfully!',
        details: `Imported 5 records from ${selectedFile.name}`
      });

      toast.success('Data imported successfully');
    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: 'Upload failed',
        details: error instanceof Error ? error.message : 'Unknown error'
      });
      toast.error('Failed to import data');
    } finally {
      setUploading(false);
    }
  };

  // AI Import handlers
  const handleImportTypeSelect = (type: ImportType) => {
    setSelectedImportType(type);
    setAiImportStep('upload');
  };

  const handleFileUploaded = (file: File, analysis: any) => {
    setFileAnalysis(analysis);
    setAiImportStep('mapping');
  };

  const handleMappingComplete = async (mappings: Record<string, string>) => {
    setAiImportStep('importing');
    
    try {
      // Call the Cloudflare Worker to start the actual import
      const response = await fetch('https://app.hivetivity.com/api/import/execute', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          analysisId: fileAnalysis?.analysisId,
          approvedMappings: mappings,
          importOptions: {
            skipErrors: true,
            createMissingParents: false,
            mergeStrategy: 'skip'
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Import started! Job ID: ${result.importJobId}`);
      } else {
        throw new Error(`Import failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Import execution failed:', error);
      toast.error('Failed to start import. Please try again.');
    }
  };

  const resetAiImport = () => {
    setAiImportStep('select');
    setSelectedImportType(null);
    setFileAnalysis(null);
  };

  return (
    <div className="container py-6">
      <StandardPageHeader 
        title="Data Import" 
        subtitle="Import families, students, and other data"
        icon={Upload}
      />

      <div className="mt-6">
        <Tabs value={activeTab || 'ai-import'} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="ai-import" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              AI Import
            </TabsTrigger>
            <TabsTrigger value="families">Families</TabsTrigger>
            <TabsTrigger value="parents">Parents</TabsTrigger>
            <TabsTrigger value="students">Students</TabsTrigger>
          </TabsList>

          <TabsContent value="ai-import" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  AI-Powered Import
                </CardTitle>
                <CardDescription>
                  Upload any file and let AI automatically map your data to Hivetivity fields
                </CardDescription>
              </CardHeader>
              <CardContent>
                {aiImportStep === 'select' && (
                  <ImportTypeSelector 
                    onSelectType={handleImportTypeSelect}
                    selectedType={selectedImportType}
                  />
                )}
                
                {aiImportStep === 'upload' && selectedImportType && organizationId && (
                  <FileUpload
                    importType={selectedImportType}
                    organizationId={organizationId}
                    onFileUploaded={handleFileUploaded}
                    onBack={resetAiImport}
                  />
                )}
                
                {aiImportStep === 'mapping' && selectedImportType && fileAnalysis && (
                  <FieldMapping
                    importType={selectedImportType}
                    fileData={{
                      headers: fileAnalysis.headers || [],
                      sampleData: fileAnalysis.sampleData || [],
                      totalRows: fileAnalysis.totalRows || 0
                    }}
                    suggestedMappings={fileAnalysis.suggestedMappings || {}}
                    workerResults={fileAnalysis.workerResults}
                    onMappingComplete={handleMappingComplete}
                    onBack={() => setAiImportStep('upload')}
                  />
                )}
                
                {aiImportStep === 'importing' && (
                  <div className="text-center space-y-4">
                    <div className="flex justify-center">
                      <div className="p-4 bg-green-100 rounded-full">
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Import Started!</h3>
                      <p className="text-gray-600">Your data is being processed in the background.</p>
                    </div>
                    <Button onClick={resetAiImport} variant="outline">
                      Start Another Import
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Legacy Manual Import Tabs */}
          {activeTab !== 'ai-import' && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Import {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</CardTitle>
                <CardDescription>
                  Upload a CSV file to import {activeTab} data into your organization
                </CardDescription>
              </CardHeader>
            <CardContent className="space-y-6">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Important</AlertTitle>
                <AlertDescription>
                  <p className="mb-2">Please make sure your CSV file follows the required format.</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Template
                  </Button>
                </AlertDescription>
              </Alert>

              <div className="grid w-full max-w-sm items-center gap-1.5">
                <Label htmlFor="fileUpload">Select CSV File</Label>
                <Input 
                  id="fileUpload" 
                  type="file" 
                  accept=".csv" 
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <p className="text-sm text-muted-foreground">
                  {selectedFile ? `Selected: ${selectedFile.name}` : 'No file selected'}
                </p>
              </div>

              {uploading && (
                <div className="space-y-2">
                  <Label>Uploading...</Label>
                  <Progress value={uploadProgress} />
                </div>
              )}

              {uploadResult && (
                <Alert variant={uploadResult.success ? "default" : "destructive"}>
                  {uploadResult.success 
                    ? <CheckCircle2 className="h-4 w-4" /> 
                    : <AlertCircle className="h-4 w-4" />
                  }
                  <AlertTitle>{uploadResult.message}</AlertTitle>
                  {uploadResult.details && (
                    <AlertDescription>{uploadResult.details}</AlertDescription>
                  )}
                </Alert>
              )}

              <Button 
                onClick={handleUpload} 
                disabled={!selectedFile || uploading}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload and Import
              </Button>
            </CardContent>
            </Card>
          )}

          <TabsContent value="families" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Family Import Guidelines</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>When importing families, please ensure your CSV contains the following columns:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>name</strong> - The family name (required)</li>
                  <li><strong>billing_status</strong> - Billing status (current, overdue, inactive)</li>
                  <li><strong>notes</strong> - Any additional notes about the family</li>
                </ul>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Note</AlertTitle>
                  <AlertDescription>
                    Importing families with the same name as existing ones will create duplicates.
                    Make sure to check existing families first.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="parents" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Parent Import Guidelines</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>When importing parents, please ensure your CSV contains the following columns:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>family_name</strong> - The name of the family this parent belongs to (required)</li>
                  <li><strong>first_name</strong> - First name of the parent (required)</li>
                  <li><strong>last_name</strong> - Last name of the parent (required)</li>
                  <li><strong>email</strong> - Email address</li>
                  <li><strong>phone</strong> - Phone number</li>
                  <li><strong>is_primary</strong> - Whether this is the primary parent (true/false)</li>
                  <li><strong>address_street</strong> - Street address</li>
                  <li><strong>address_city</strong> - City</li>
                  <li><strong>address_state</strong> - State/Province</li>
                  <li><strong>address_zip</strong> - ZIP/Postal code</li>
                  <li><strong>address_country</strong> - Country (defaults to United States)</li>
                </ul>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Note</AlertTitle>
                  <AlertDescription>
                    The family_name must match an existing family. If no match is found, the parent will not be imported.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="students" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Student Import Guidelines</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>When importing students, please ensure your CSV contains the following columns:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>family_name</strong> - The name of the family this student belongs to (required)</li>
                  <li><strong>first_name</strong> - First name of the student (required)</li>
                  <li><strong>last_name</strong> - Last name of the student (required)</li>
                  <li><strong>date_of_birth</strong> - Date of birth (YYYY-MM-DD format)</li>
                  <li><strong>gender</strong> - Gender (M/F/Other)</li>
                  <li><strong>notes</strong> - Any additional notes about the student</li>
                </ul>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Note</AlertTitle>
                  <AlertDescription>
                    The family_name must match an existing family. If no match is found, the student will not be imported.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DataImport; 